import { ALargeSmall, CircleHelp, LogOut, Menu, RefreshCw, Smartphone } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ROUTES } from '@/config/routes';
import { MEMBER_AXIS_LABELS } from '@/config/workspace-features';
import { WORKSPACE_NAV } from '@/config/workspace-nav';
import { DisplayNameDialog } from '@/features/account/components/DisplayNameDialog';
import { NotificationSettingsDialog } from '@/features/notification/components/NotificationSettingsDialog';
import { useIsMobileDevice } from '@/features/mobile/hooks/useIsMobileDevice';
import { useSwitchToMobile } from '@/features/mobile/hooks/useSwitchToMobile';
import { YearSelector } from '@/features/program-year/components/YearSelector';
import { useFeature } from '@/hooks/useFeature';
import { useRouteHandle } from '@/hooks/useRouteHandle';
import { useWorkspaceHomeLink } from '@/layouts/workspace/useWorkspaceNav';
import { cn } from '@/lib/utils';
import { queryClient } from '@/lib/queryClient';
import { useAuthStore } from '@/stores/auth';
import { FONT_SIZE_OPTIONS, usePreferencesStore, type FontSizeLevel } from '@/stores/preferences';

interface WorkspaceTopbarProps {
  onOpenMenu: () => void;
}

/**
 * 상단바(Top) — 모바일 메뉴 버튼 + 브레드크럼(워크스페이스 › 메뉴 › 현재 페이지) + 우측 액션(기준 연도·사용 안내·새로고침·계정).
 * 기준 연도 선택기는 연도 기반 메뉴 전체가 공유하는 단일 제어점이다(specs/032 FR-013).
 * 계정 메뉴에 글자 크기 3단계(루트 font-size 배율, localStorage 저장). Admin Kit header·nav-user 구성.
 * 브레드크럼의 메뉴명은 workspace-nav 매칭, 현재 페이지명은 route handle.title.
 * 모바일 기기에서는 메뉴 버튼 옆에 "모바일 화면으로 보기" 버튼(specs/037 FR-004)을 드러내 둔다 — 모바일 화면의 "PC 버전" 버튼과 짝이며,
 * 계정 메뉴 안에 숨기면 PC 로 넘어온 사용자가 돌아갈 길을 찾지 못한다. 좌측 묶음에 둬 좁은 화면에서도 밀려나지 않는다.
 */
export function WorkspaceTopbar({ onOpenMenu }: WorkspaceTopbarProps) {
  const { pathname } = useLocation();
  const { title, yearIndependent } = useRouteHandle();
  const navigate = useNavigate();
  const name = useAuthStore((s) => s.name);
  const email = useAuthStore((s) => s.email);
  const axis = useAuthStore((s) => s.axis);
  const canSubscribe = useFeature('SUBSCRIPTION');
  // 연도 기준은 사업·공고 판정 화면에만 의미가 있다 — 그 기능이 없는 회원에게는 숨긴다(specs/065).
  const canPickYear = useFeature('PROGRAM_DETAIL');
  const logout = useAuthStore((s) => s.logout);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const fontSize = usePreferencesStore((s) => s.fontSize);
  const setFontSize = usePreferencesStore((s) => s.setFontSize);
  const isMobileDevice = useIsMobileDevice();
  const switchToMobile = useSwitchToMobile();
  const homeLink = useWorkspaceHomeLink();

  const section = WORKSPACE_NAV.find(
    (i) => pathname === i.to || pathname.startsWith(`${i.match ?? i.to}/`),
  );
  const showPage = Boolean(title && title !== section?.label);
  const crumbs = [
    { label: '워크스페이스', to: homeLink },
    ...(section ? [{ label: section.label, to: section.to }] : []),
    ...(showPage && title ? [{ label: title, to: undefined }] : []),
  ];
  const initial = ((name ?? email ?? '').trim().charAt(0) || '?').toUpperCase();

  async function onLogout() {
    await logout();
    navigate(ROUTES.home);
  }

  return (
    <header className="flex h-16 items-center justify-between gap-4 border-b bg-card px-4 sm:px-8">
      <div className="flex min-w-0 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 lg:hidden"
          aria-label="메뉴 열기"
          onClick={onOpenMenu}
        >
          <Menu />
        </Button>
        {isMobileDevice && (
          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="모바일 화면으로 보기"
            title="모바일 화면으로 보기"
            onClick={switchToMobile}
          >
            <Smartphone />
          </Button>
        )}
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap">
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <Fragment key={c.label}>
                  {i > 0 && <BreadcrumbSeparator className="hidden sm:block" />}
                  {/* 좁은 화면은 현재 페이지만 — 상위 경로는 sm 이상에서(Admin Kit header 관례) */}
                  <BreadcrumbItem
                    className={cn('min-w-0 whitespace-nowrap', !last && 'hidden sm:inline-flex')}
                  >
                    {last || !c.to ? (
                      <BreadcrumbPage className="truncate font-medium">{c.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={c.to}>{c.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {canPickYear && <YearSelector independent={yearIndependent} />}
        {/* 좁은 화면(sm 미만)은 사용 안내·새로 고침을 접는다 — 계정 메뉴가 화면 밖으로 밀려나지 않게.
            사용 안내는 좌측 메뉴, 새로 고침은 브라우저 새로 고침으로 대신한다. */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:inline-flex"
          aria-label="사용 안내"
          title="사용 안내"
          asChild
        >
          <Link to={ROUTES.workspace.guide}>
            <CircleHelp />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:inline-flex"
          aria-label="새로 고침"
          onClick={() => queryClient.invalidateQueries()}
        >
          <RefreshCw />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-auto gap-2 px-2 py-1.5" aria-label="계정 메뉴">
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium sm:inline">{name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-medium">{name}</p>
              <p className="truncate text-xs text-muted-foreground">{email}</p>
              {axis && (
                <p className="mt-2 text-xs text-muted-foreground">
                  회원 유형{' '}
                  <span className="font-medium text-foreground">{MEMBER_AXIS_LABELS[axis]}</span>
                  <span className="block">바꾸려면 고객센터로 문의해 주세요.</span>
                </p>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setNameDialogOpen(true)}>
              표시 이름 변경
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setNotifyDialogOpen(true)}>
              알림 설정
            </DropdownMenuItem>
            {canSubscribe && (
              <DropdownMenuItem asChild>
                <Link to={ROUTES.workspace.subscription}>구독 관리</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to={ROUTES.home}>홈페이지로</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <ALargeSmall className="size-4" aria-hidden="true" />
              글자 크기
            </DropdownMenuLabel>
            {/* 라디오 — 선택해도 메뉴를 닫지 않아 커진 화면을 바로 비교할 수 있다 */}
            <DropdownMenuRadioGroup
              value={fontSize}
              onValueChange={(v) => setFontSize(v as FontSizeLevel)}
            >
              {FONT_SIZE_OPTIONS.map((o) => (
                <DropdownMenuRadioItem
                  key={o.value}
                  value={o.value}
                  onSelect={(e) => e.preventDefault()}
                >
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onLogout}>
              <LogOut />
              로그아웃
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DisplayNameDialog open={nameDialogOpen} onOpenChange={setNameDialogOpen} />
        {notifyDialogOpen && (
          <NotificationSettingsDialog onClose={() => setNotifyDialogOpen(false)} />
        )}
      </div>
    </header>
  );
}
